import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';

const testimonialSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  text: z.string().min(1, 'Text is required'),
});

const TestimonialModal = ({
  type = 'testimonial',
  isOpen,
  onClose,
  onSubmit,
  editingTestimonial = null,
}) => {
  const form = useForm({
    resolver: zodResolver(testimonialSchema),
    defaultValues: {
      name: editingTestimonial?.name || '',
      text: editingTestimonial?.text || '',
    },
  });

  useEffect(() => {
    if (editingTestimonial) {
      form.reset({
        name: editingTestimonial?.name || '',
        text: editingTestimonial?.text || '',
      });
    } else {
      form.reset({
        name: '',
        text: '',
      });
    }
  }, [editingTestimonial, form]);

  const handleSubmit = (data, event) => {
    event.preventDefault();
    event.stopPropagation();
    onSubmit(data);
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-xl p-4"
        style={{ position: 'fixed', zIndex: 50 }}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="text-darkPurple mt-1 mb-4 text-base font-semibold p-1 bg-gray-100 rounded">
            {editingTestimonial
              ? type == 'successStories'
                ? 'Edit Story'
                : 'Edit Testimonial'
              : type === 'successStories'
                ? 'Add Success Story'
                : 'Add Testimonial'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit((data) => handleSubmit(data, e))(e);
            }}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Name of the Person"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0  text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Add Text</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter text"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0  text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-center pt-4">
              <Button
                type="submit"
                className="bg-darkPurple hover:bg-darkerPurple px-6 transition-all ease-in duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-darkPurple disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingTestimonial ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TestimonialModal;
